/**
 * How a list joined by AND and OR reads (M4S-5777 X39): AND binds tighter than OR, the way a
 * person reads "Q1 is 1 AND Q2 is 2 OR Q3 is 3" — (Q1 is 1 AND Q2 is 2) OR Q3 is 3. Pure.
 *
 * Each item says how it joins the NEXT one (`'all'` = AND, `'any'` = OR); the last item's join is
 * ignored. A missing join reads as `fallback` (AND for live filters, so every filter saved before
 * filters could be joined keeps meaning what it meant: all of them at once).
 *
 * This is the one home of that precedence. Filters fold through it today. A variable's conditions
 * still fold strictly left to right (`evalBucket` in conditions.ts) because saved variables count
 * that way; X46 kept that fold and added explicit groups instead ((A OR B) AND NOT (C), shown as
 * written; docs/pdlc/crosstabs-variables.md §X46), so no saved variable's meaning moved.
 * Spec: docs/pdlc/crosstabs-filters.md §3.2.
 */

export type Join = 'all' | 'any';

/**
 * The items split into OR-groups, each an AND-chain, in order. `[[a, b], [c]]` reads
 * "(a AND b) OR c".
 */
export function joinGroups<T>(
    items: readonly T[],
    joinOf: (item: T) => Join | undefined,
    fallback: Join = 'all',
): T[][] {
    const groups: T[][] = [];
    let current: T[] = [];
    items.forEach((item, i) => {
        current.push(item);
        const last = i === items.length - 1;
        if (!last && (joinOf(item) ?? fallback) === 'any') {
            groups.push(current);
            current = [];
        }
    });
    if (current.length) groups.push(current);
    return groups;
}

/**
 * Whether the joined list holds: some OR-group whose every item passes `test`. An empty list holds
 * (nothing to fail). Short-circuits, so `test` runs only as often as it must.
 */
export function foldJoins<T>(
    items: readonly T[],
    test: (item: T) => boolean,
    joinOf: (item: T) => Join | undefined,
    fallback: Join = 'all',
): boolean {
    if (items.length === 0) return true;
    return joinGroups(items, joinOf, fallback).some((group) => group.every(test));
}
